/**
 * Copyright (c) 2025 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';

import { useState, useContext } from 'react';
import { completeTask } from 'blb-api/tasks';
import { errorMessage } from 'blb-utils/errors';
import { get } from 'lodash';
import { MeContext } from 'blb-contexts/Me';

import { Form } from 'react-final-form';
import { Text } from '@blend/enterprise-components';
import { Task } from './Task';
import { TaskModal } from '../TaskModal';
import { BlendBridge } from 'blb-types/blendBridge';
export namespace ReleaseClosingDocumentsTask {
  export interface Props extends Omit<BlendBridge.API.ReleaseClosingDocumentsTask, 'type'> {
    taskId: string;
    onSuccess: () => Promise<any>;
  }
}

export const ReleaseClosingDocumentsTask: React.FC<ReleaseClosingDocumentsTask.Props> = ({
  disabledReason,
  isDisabled,
  taskId,
  onSuccess,
}) => {
  const me = useContext(MeContext);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleFormSubmit = async () => {
    if (taskId) {
      try {
        setLoading(true);
        // hit the external service to complete the task
        await completeTask(taskId);
        await onSuccess();
      } catch (err) {
        setError(get(err, 'response.data.error.message', errorMessage('SENDING_FORM', me?.principalType)));
        setLoading(false);
        setIsModalOpen(false);
      }
    }
    setIsModalOpen(false);
  };

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setError(undefined);
    setIsModalOpen(false);
  };

  const taskSubheader =
    'This closing requires borrower(s) to sign their e-sign package at a physical branch. Confirm borrowers are present before releasing documents for them to sign.';

  return (
    <Task
      buttonLabel={'Release documents for signing'}
      disabledReason={disabledReason}
      displayType={'verticalCard'}
      error={error}
      isDisabled={isDisabled || isLoading}
      onClick={handleClick}
      taskHeader={'Release documents for signing'}
      taskId={taskId}
      taskSubheader={taskSubheader}
      type={BlendBridge.TaskTypes.RELEASE_CLOSING_DOCUMENTS}
    >
      <Form onSubmit={handleFormSubmit}>
        {({ handleSubmit }) => {
          return (
            <TaskModal
              alternateButtonLabel="Cancel"
              divideFooter={true}
              error={error}
              handleAlternateClick={handleClose}
              handleClose={handleClose}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
              isOpen={isModalOpen}
              submitButtonLabel="Confirm and send documents"
              taskId={taskId}
              title="Confirm borrower arrival"
            >
              <div style={{ margin: '0 auto' }}>
                <div style={{ margin: '0 auto', textAlign: 'left' }}>
                  <Text color="secondary" fontSize="md">
                    Please confirm that the borrower is present before proceeding. After confirmation, borrowers will
                    receive an email to login to their portal to sign their e-sign documents.
                  </Text>
                </div>
              </div>
            </TaskModal>
          );
        }}
      </Form>
    </Task>
  );
};
